import { getDistance } from "geolib";
import { writable } from "svelte/store";
import { cairns as cairnsAt } from "../api/api.js";
import persisted from "./common/persisted.js";
import { journeySubscribeThrottled } from "./journey.js";

const MINIMUM_DISTANCE_INTERVAL_METERS = 1000;
export const MINIMUM_CAIRN_BUILD_INTERVAL_MS = 60_000;

function isSameCairn(current, next) {
	if (current.id !== undefined && next.id !== undefined) {
		return current.id === next.id;
	}

	if (current.slug !== undefined && next.slug !== undefined) {
		return current.slug === next.slug;
	}

	if (
		current.transaction_id !== undefined &&
		next.transaction_id !== undefined
	) {
		return current.transaction_id === next.transaction_id;
	}

	return false;
}

function createCairnsStore() {
	const store = writable({
		all: [],
		lastBuiltAt: -MINIMUM_CAIRN_BUILD_INTERVAL_MS,
		name: "",
		buildCount: 0
	});

	const { subscribe, update, set } = store;

	let lastUpdatedAt;

	journeySubscribeThrottled(9000, async ({ location }) => {
		const shouldUpdate =
			location &&
			(lastUpdatedAt === undefined ||
				getDistance(lastUpdatedAt, location) >
					MINIMUM_DISTANCE_INTERVAL_METERS);

		if (!shouldUpdate) {
			return;
		}

		lastUpdatedAt = location;

		try {
			const { cairns } = await cairnsAt(location);

			if (cairns.length > 0) {
				update((state) => ({
					...state,
					all: Object.values(
						[...state.all, ...cairns].reduce(
							(result, cairn) => ({
								...result,
								[cairn.id]: cairn
							}),
							{}
						)
					)
				}));
			}
		} catch (error) {
			console.warn("failed to fetch cairns", error);
		}
	});

	return {
		subscribe,
		set(state) {
			set({
				lastBuiltAt: 0,
				...state,
				all: state.all.filter((c) => !!c.id)
			});
		},
		add(cairn, time, name) {
			update((state) => ({
				all: [...state.all, cairn],
				lastBuiltAt: time || state.lastBuiltAt,
				name: name || state.name,
				buildCount: state.buildCount + 1
			}));
		},
		remember(nextCairn) {
			if (nextCairn === undefined || nextCairn === null) {
				return;
			}

			update((state) => {
				const hasMatch =
					state.all.some((current) => isSameCairn(current, nextCairn)) ===
					true;

				if (hasMatch === false) {
					return {
						...state,
						all: [...state.all, nextCairn]
					};
				}

				return {
					...state,
					all: state.all.map((current) => {
						if (isSameCairn(current, nextCairn) === false) {
							return current;
						}

						return { ...current, ...nextCairn };
					})
				};
			});
		},
		refresh(cairn) {
			update((state) => ({
				all: state.all.map((c) => (c.id === cairn.id ? cairn : c))
			}));
		}
	};
}

const cairn = persisted("cairns:v12", true, createCairnsStore());

export default cairn;
